/**
 * Punkt 150 P3 — Veröffentlichen-Dialog + Statuszeile (Mockup CCFkKsB5KKG9xA7VbWnLnW, Tab 3).
 *
 * Abweichung zum Mockup (Entscheidung 06.10.2026): fehlende Sprachen sind eine SPERRE, keine
 * Warnung. „Veröffentlichen“ fordert dann nur an; der Server übersetzt im Hintergrund und
 * veröffentlicht danach selbst. Preissprünge > 30 % nur gelb markiert (nur Verwaltung).
 */
import { useState } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Drawer, ErrorNote, LoadingRows, useBestaetigung } from "./components";
import { formatDateTime, money } from "./format";

export type UebersetzungStatus = "offen" | "fertig" | "haengt" | null;
export type Veroeffentlichungen = {
  cardId: string;
  name: string;
  publishedVersion: number | null;
  publishedAt: string | null;
  uebersetzung: { status: UebersetzungStatus; versuche: number; fehler: string | null };
  wartetAufUebersetzung: boolean;
  versionen: { version: number; publishedAt: string; publishedBy: string | null }[];
  vorschau: {
    fehlend: { art: string; id: string; name: string; sprachen: string[] }[];
    aenderungen: {
      neu: string[];
      entfernt: string[];
      geaendert: string[];
      preise: { name: string; altCents: number; neuCents: number; prozent: number | null; preisSprung: boolean }[];
    };
  };
};

const SPRACHE: Record<string, string> = { en: "Englisch", it: "Italienisch", fr: "Französisch" };

export function uebersetzungText(status: UebersetzungStatus, wartet: boolean): string | null {
  if (status === "haengt") return "Übersetzung hängt, alte Version bleibt online";
  if (status === "offen" || wartet) return "Übersetzung läuft";
  if (status === "fertig") return "Übersetzung fertig";
  return null;
}

/** Dezente Zeile ganz unten: Version · veröffentlicht am · Übersetzung. */
export function statusZeile(d: Pick<Veroeffentlichungen, "publishedVersion" | "publishedAt" | "uebersetzung" | "wartetAufUebersetzung">): string {
  const teile = [
    d.publishedVersion ? `Version ${d.publishedVersion} · veröffentlicht am ${formatDateTime(d.publishedAt)}` : "Noch nicht veröffentlicht",
  ];
  const u = uebersetzungText(d.uebersetzung.status, d.wartetAufUebersetzung);
  if (u) teile.push(u);
  return teile.join(" · ");
}

function fehlendNachSprache(fehlend: Veroeffentlichungen["vorschau"]["fehlend"]): [string, number][] {
  const z = new Map<string, number>();
  for (const f of fehlend) for (const s of f.sprachen) z.set(s, (z.get(s) ?? 0) + 1);
  return [...z.entries()];
}

const K = "rounded-md px-2 py-0.5 font-label-md text-[11px] font-semibold";
const GELB = "rounded px-1 bg-[#F0EAE0] text-secondary outline outline-1 outline-outline dark:bg-white/[0.08] dark:text-[#b8ae9e]";

export function VeroeffentlichenDrawer({ cardId, onClose, onChanged }: { cardId: string | null; onClose: () => void; onChanged?: () => void }) {
  const { data: d, loading, error, reload } = useApi<Veroeffentlichungen>(cardId ? `/api/menu-cards/${cardId}/publications` : null);
  const { bestaetigen, dialog } = useBestaetigung();
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  function schliessen() {
    setFehler(null);
    setMeldung(null);
    onClose();
  }

  async function veroeffentlichen() {
    if (!cardId) return;
    setBusy(true);
    setFehler(null);
    try {
      const r = await apiMutate<{ status: string; version?: number }>(`/api/menu-cards/${cardId}/publish`, "POST", {});
      setMeldung(
        r.status === "veroeffentlicht"
          ? `Veröffentlicht · Version ${r.version}`
          : "Übersetzung läuft. Die Karte geht danach von selbst online, bis dahin bleibt die bisherige Version sichtbar.",
      );
      reload();
      onChanged?.();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function zurueckholen(version: number) {
    if (!cardId) return;
    const ok = await bestaetigen({
      titel: `Version ${version} zurückholen?`,
      text: "Ihr Inhalt wird als neue Version veröffentlicht. Nichts wird gelöscht, die jetzige Version bleibt im Verlauf.",
      bestaetigen: "Zurückholen",
    });
    if (!ok) return;
    setBusy(true);
    setFehler(null);
    try {
      const r = await apiMutate<{ version: number }>(`/api/menu-cards/${cardId}/publications/${version}/restore`, "POST", {});
      setMeldung(`Version ${version} zurückgeholt · jetzt Version ${r.version}`);
      reload();
      onChanged?.();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const a = d?.vorschau.aenderungen;
  const fehlSprachen = d ? fehlendNachSprache(d.vorschau.fehlend) : [];
  const spruenge = a?.preise.filter((p) => p.preisSprung) ?? [];

  return (
    <Drawer
      open={cardId != null}
      onClose={schliessen}
      title={d ? `${d.name} veröffentlichen` : "Veröffentlichen"}
      subtitle={d?.publishedVersion ? `Version ${d.publishedVersion + 1} ersetzt Version ${d.publishedVersion} (veröffentlicht am ${formatDateTime(d.publishedAt)}).` : undefined}
      footer={
        d && (
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            <span className="mr-auto rounded-full bg-[#F0EAE0] px-2.5 py-0.5 font-label-md text-[11px] font-semibold text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]">
              {d.publishedVersion ? `Ersetzt „${d.name}“` : `Erste Veröffentlichung von „${d.name}“`}
            </span>
            <Button variant="secondary" onClick={schliessen}>Abbrechen</Button>
            <Button disabled={busy || (d.wartetAufUebersetzung && d.uebersetzung.status !== "haengt")} onClick={() => void veroeffentlichen()}>
              {fehlSprachen.length ? "Übersetzen und veröffentlichen" : "Jetzt veröffentlichen"}
            </Button>
          </div>
        )
      }
    >
      {dialog}
      {loading && !d && <LoadingRows />}
      {error && <ErrorNote message={error} />}
      {fehler && <ErrorNote message={fehler} />}
      {d && a && (
        <div className="flex flex-col gap-4">
          {meldung && <p className="font-semibold text-secondary dark:text-[#b8ae9e]" role="status">{meldung}</p>}

          <div className="grid grid-cols-3 gap-2">
            {([["neu", a.neu.length], ["geändert", a.geaendert.length + a.preise.length], ["entfernt", a.entfernt.length]] as const).map(([l, n]) => (
              <div key={l} className="rounded-xl border border-surface-variant px-3 py-2 dark:border-white/10">
                <b className="block text-[22px]">{n}</b>{l}
              </div>
            ))}
          </div>

          {(fehlSprachen.length > 0 || spruenge.length > 0 || d.uebersetzung.status === "haengt") && (
            <div className="flex flex-col gap-1.5 rounded-xl border border-outline bg-[#F0EAE0] px-3.5 py-3 text-secondary dark:border-white/10 dark:bg-white/[0.08] dark:text-[#b8ae9e]">
              {d.uebersetzung.status === "haengt" && (
                <div><b>Übersetzung hängt, alte Version bleibt online.</b> Ein neuer Versuch startet beim nächsten Veröffentlichen.</div>
              )}
              {fehlSprachen.map(([s, n]) => (
                <div key={s}>{SPRACHE[s] ?? s}: <b>{n} {n === 1 ? "Text fehlt" : "Texte fehlen"}.</b></div>
              ))}
              {fehlSprachen.length > 0 && (
                <div className="text-[13px]">Die Karte wird erst übersetzt und geht danach von selbst online. Bis dahin bleibt die bisherige Version sichtbar.</div>
              )}
              {spruenge.map((p) => (
                <div key={p.name}>Preissprung über 30 %: {p.name} von {money(p.altCents)} auf {money(p.neuCents)}.</div>
              ))}
            </div>
          )}

          <div>
            <h3 className="mb-1.5 text-[13px] uppercase tracking-wider text-secondary">Änderungen gegenüber der veröffentlichten Karte</h3>
            <ul className="flex flex-col">
              {a.neu.map((n) => (
                <li key={`n-${n}`} className="border-b border-surface-variant py-1.5 dark:border-white/10"><span className={`${K} mr-2 bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]`}>neu</span>{n}</li>
              ))}
              {a.preise.map((p) => (
                <li key={`p-${p.name}`} className="flex flex-wrap justify-between gap-2 border-b border-surface-variant py-1.5 dark:border-white/10">
                  <span><span className={`${K} mr-2 border border-surface-variant`}>Preis</span>{p.name}</span>
                  <span>
                    <del className="text-secondary">{money(p.altCents)}</del> → <b>{money(p.neuCents)}</b>{" "}
                    {p.prozent !== null && <span className={p.preisSprung ? GELB : "text-secondary"}>{p.prozent > 0 ? "+" : ""}{p.prozent} %</span>}
                  </span>
                </li>
              ))}
              {a.geaendert.map((n) => (
                <li key={`g-${n}`} className="flex flex-wrap justify-between gap-2 border-b border-surface-variant py-1.5 dark:border-white/10">
                  <span><span className={`${K} mr-2 border border-surface-variant`}>geändert</span>{n}</span>
                  <span className="text-secondary">Text oder Allergene angepasst</span>
                </li>
              ))}
              {a.entfernt.map((n) => (
                <li key={`e-${n}`} className="border-b border-surface-variant py-1.5 dark:border-white/10"><span className={`${K} mr-2 bg-error/10 text-error`}>entfernt</span>{n}</li>
              ))}
              {!a.neu.length && !a.preise.length && !a.geaendert.length && !a.entfernt.length && (
                <li className="text-secondary">Keine Änderungen gegenüber der veröffentlichten Version.</li>
              )}
            </ul>
          </div>

          {d.versionen.length > 0 && (
            <div>
              <h3 className="mb-1.5 text-[13px] uppercase tracking-wider text-secondary">Versionen</h3>
              <ul className="flex flex-col">
                {d.versionen.map((v) => (
                  <li key={v.version} className="flex items-center justify-between gap-2 border-b border-surface-variant py-1.5 dark:border-white/10">
                    <span>Version {v.version} · {formatDateTime(v.publishedAt)}{v.version === d.publishedVersion && " · online"}</span>
                    {v.version !== d.publishedVersion && (
                      <Button variant="secondary" disabled={busy} onClick={() => void zurueckholen(v.version)}>Version zurückholen</Button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="mt-2 text-[11px] text-secondary opacity-80" data-testid="veroeffentlichung-status">{statusZeile(d)}</p>
        </div>
      )}
    </Drawer>
  );
}
